import { useCallback, useEffect, useMemo, useState } from 'react'

import { FeatureFlagName } from '@api'
import { useUnit } from 'effector-react'
import { Helmet } from 'react-helmet-async'

import PhoneIcon from '@/assets/icons/icon-phone.svg'
import ReferralIcon from '@/assets/icons/icon-referral.svg'
import { Button } from '@/components/ui/button.tsx'
import { $account } from '@/models/account.ts'
import { $availableFeatures } from '@/models/feature-flags'
import { useAccount } from '@/modules/accounts/hooks/useAccounts.ts'
import { AccountMenu } from '@/modules/accounts/ui/AccountMenu'
import { CampaignCountdown } from '@/modules/campaigns/components/countdown'
import { MID_YEAR_SALE_END } from '@/modules/mid-year-sale/consts'
import { MidYearSaleDialogTrigger } from '@/modules/mid-year-sale/ui/dialog-trigger'
import { OnBoardingCallPopup } from '@/modules/onboarding-call/OnBoardingCallPopup'
import { ReferralDialog } from '@/modules/referral/referral-dialog.tsx'
import { SUMMER_DEAL_END } from '@/modules/summer-deal/consts'
import { SummerDealButton } from '@/modules/summer-deal/ui/SummerDealButton'
import { SummerDealCountdown } from '@/modules/summer-deal/ui/SummerDealCountdown'
import { isFeatureAvailable } from '@/utils/featureFlags'

import { LicenseValidationHandler } from './LicenseValidationHandler'

export const HeaderMenu = () => {
  const [showReferral, setShowReferral] = useState(false)
  const [showOnboardingCall, setShowOnboardingCall] = useState(false)
  const availableFeatures = useUnit($availableFeatures)

  const account = useUnit($account)
  const { updateUserSettings, userSettings, owner } = useAccount()
  const hasSeenReferrals = userSettings?.seenReferralsPopup ?? false
  const canSeeReferrals = false

  const handleSubmitted = useCallback(() => {
    updateUserSettings({ seenReferralsPopup: true })
  }, [updateUserSettings])

  const showMidYearSaleOffer = useMemo(
    () => MID_YEAR_SALE_END > +new Date(),
    [],
  )

  const showSummerDeal = useMemo(() => SUMMER_DEAL_END > +new Date(), [])
  const isOnboardingAvailable = isFeatureAvailable(
    availableFeatures,
    FeatureFlagName.ONBOARDING,
    false,
  )

  useEffect(() => {
    if (!owner || !userSettings || !account) return
    if (account.role !== 'OWNER') return
    if (!isOnboardingAvailable) return

    if (owner.openOnboardingPopup) {
      setShowOnboardingCall(true)
    }
  }, [account, canSeeReferrals, owner, userSettings, isOnboardingAvailable])

  return (
    <>
      <nav className="flex items-center gap-4">
        {owner?.showOnboardingButton && isOnboardingAvailable && (
          <Button
            className="border-2 border-orange-300 bg-orange-200 text-text-default hover:bg-orange-300 dark:border-orange-400 dark:bg-orange-500 dark:hover:bg-orange-400"
            onClick={() => setShowOnboardingCall(true)}
          >
            <PhoneIcon className="mr-2 h-4 w-4 text-text-default" />
            Free Onboarding
          </Button>
        )}
        {showMidYearSaleOffer && (
          <>
            <Helmet>
              <style>{`
                @font-face {
                  font-family: "Blender Pro";
                  src: url("/fonts/BlenderPro-Medium.ttf") format("truetype");
                  font-weight: 400;
                  font-style: normal;
                  font-display: swap;
                }
              `}</style>
            </Helmet>
            <div className="flex items-end gap-3">
              <MidYearSaleDialogTrigger />
              <CampaignCountdown
                end={MID_YEAR_SALE_END}
                className="bg-[linear-gradient(90deg,#2A2E66_0%,#3846A6_100%)] font-blenderPro"
              />
            </div>
          </>
        )}
        {showSummerDeal && (
          <div className="flex items-end gap-3">
            <SummerDealButton />
            <SummerDealCountdown />
          </div>
        )}
        {account && <LicenseValidationHandler />}
        {canSeeReferrals && (
          <div className="relative">
            <button
              onClick={() => setShowReferral(true)}
              className="flex h-[40px] cursor-pointer items-center gap-2 rounded-[6px] border-2 border-teal-500 bg-teal-100 px-4 shadow-none dark:border-0 dark:bg-teal-700/80"
            >
              <ReferralIcon className="h-[16px] w-[16px] text-secondary-500 dark:text-teal-600" />
              <span className="hidden text-sm font-medium text-secondary-500 dark:text-teal-500 md:block">
                Referral
              </span>
            </button>
            {!hasSeenReferrals && (
              <div className="absolute -right-1 -top-1">
                <div className="relative flex h-3 w-3">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary-400 opacity-75" />
                  <span className="relative inline-flex h-3 w-3 rounded-full bg-primary-500" />
                </div>
              </div>
            )}
          </div>
        )}
        <AccountMenu />
      </nav>

      <ReferralDialog
        isOpen={showReferral}
        onClose={() => setShowReferral(false)}
        onSubmitted={handleSubmitted}
      />
      <OnBoardingCallPopup
        isOpen={showOnboardingCall}
        handleClose={() => setShowOnboardingCall(false)}
      />
    </>
  )
}
